import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalBody,
  Flex,
  ModalHeader,
  Text,
  Progress,
  useToast,
} from '@chakra-ui/react';
import Dropzone from 'react-dropzone';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { FiUploadCloud } from 'react-icons/fi';

import {
  PLAN_REVAMP_RADIUS,
  PLAN_REVAMP_RADIUS_SM,
  PLAN_TEXT,
} from 'pages/PlanRevamp/PlanPageUIRevamp/planPageRevampUi';
import { getDataImportFiles, uploadDataImportFile, resetDataImport } from 'redux/DataImportSlice';
import { importAcceptedFormat } from 'utils/FileFormats';
import CustomToast from 'components/CustomToast';
import { AppDispatch } from 'utils/GlobalHelpers';
import {
  apiFileTypeForUpload,
  DATA_IMPORT_SUPPORTED_LABEL,
  getDataImportUploadKind,
  uploadCsvDataImportFile,
  uploadRawDataImportFile,
} from 'utils/dataImportUpload';

import colors from './dataTheme';

const AddSourceModal = ({ onClose, isOpen }: any) => {
  const { modelId } = useParams();
  const toast = useToast();
  const dispatch: AppDispatch = useDispatch();
  const replaceSpecialChars = (inputString: string) => {
    const specialCharsMap: any = {
      '!': 'exclamation_mark',
      '@': 'at_sign',
      '#': 'hash',
      '%': 'percent',
      '^': 'caret',
      '&': 'ampersand',
      '*': 'asterisk',
      '-': '',
      '/(/': '',
      '/)/': '',
      '/': '',
    };

    return inputString.replace(/[!#$%&()*/@[^{}]/g, (match) => specialCharsMap[match]);
  };

  const [loader, setLoader] = useState(false);
  const [progress, setProgress] = useState(0);

  const handleUpload = async (file: File) => {
    if (file.size > 10 * 1024 * 1024) {
      toast({
        duration: 2500,
        render: () => <CustomToast title='File size exceeds 10MB limit.' status='error' />,
      });
      return;
    }

    const kind = getDataImportUploadKind(file);
    if (!kind) {
      toast({
        duration: 2500,
        render: () => (
          <CustomToast
            title={`Please upload a ${DATA_IMPORT_SUPPORTED_LABEL} file.`}
            status='error'
          />
        ),
      });
      return;
    }

    setLoader(true);
    try {
      const s3Url =
        kind === 'csv'
          ? await uploadCsvDataImportFile(file, 'blox-data-import', setProgress)
          : await uploadRawDataImportFile(file, 'blox-data-import', setProgress);

      const fileName = file.name
        .split(' ')
        .map((subString: string) => replaceSpecialChars(subString))
        .join('');

      const requestBody = {
        file_name: fileName,
        file_type: apiFileTypeForUpload(kind, file.type),
        s3_file_url: s3Url,
      };

      await dispatch(uploadDataImportFile({ modelId, request_body: requestBody, toaster: true }));
      await dispatch(getDataImportFiles(modelId));
      await dispatch(resetDataImport());
      onClose();
    } catch (error) {
      const message =
        error instanceof Error && error.message === 'EMPTY_FILE'
          ? 'The uploaded file is empty.'
          : 'Upload failed. Please try again.';
      toast({
        duration: 2500,
        render: () => <CustomToast title={message} status='error' />,
      });
    } finally {
      setLoader(false);
      setProgress(0);
    }
  };

  const handleFileDrop = (acceptedFiles: File[], rejectedFiles: any) => {
    if (rejectedFiles.length > 0) {
      toast({
        duration: 2500,
        render: () => (
          <CustomToast
            title={`Please upload a ${DATA_IMPORT_SUPPORTED_LABEL} file.`}
            status='error'
          />
        ),
      });
      return;
    }

    if (acceptedFiles[0]) {
      handleUpload(acceptedFiles[0]);
    }
  };

  return (
    <Modal
      onClose={() => {
        onClose();
        setLoader(false);
      }}
      isOpen={isOpen}
      isCentered
    >
      <ModalOverlay />
      <ModalContent
        maxW='32rem'
        borderRadius={PLAN_REVAMP_RADIUS}
        bg={colors.surface}
        color={colors.textDark}
      >
        <ModalHeader pb={1}>
          <Text {...PLAN_TEXT.modalTitle} color={colors.textDark}>
            Add source
          </Text>
          <Text {...PLAN_TEXT.modalSubtitle} color={colors.textSecondary} fontWeight={400}>
            Upload a file to use as a data source for this model.
          </Text>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          {loader ? (
            <Flex direction='column' alignItems='center' justifyContent='center' py={10} gap={3}>
              <Text {...PLAN_TEXT.bodyMedium} color={colors.textDark}>
                {`Uploading… ${progress}%`}
              </Text>
              <Progress
                w='80%'
                value={progress}
                size='sm'
                borderRadius='full'
                colorScheme='purple'
                bg={colors.activeBg}
              />
            </Flex>
          ) : (
            <Dropzone
              onDrop={(acceptedFiles, rejectedFiles) =>
                handleFileDrop(acceptedFiles, rejectedFiles)
              }
              multiple={false}
              accept={importAcceptedFormat}
            >
              {({ getRootProps, getInputProps, isDragActive }) => (
                <Flex
                  direction='column'
                  align='center'
                  justify='center'
                  gap={2}
                  py={10}
                  px={6}
                  cursor='pointer'
                  border='1px dashed'
                  borderColor={isDragActive ? colors.primary : colors.border}
                  bg={isDragActive ? colors.activeBg : 'transparent'}
                  borderRadius={PLAN_REVAMP_RADIUS_SM}
                  transition='background 0.12s ease, border-color 0.12s ease'
                  _hover={{ bg: colors.hover, borderColor: colors.primary }}
                  {...getRootProps({ className: 'dropzone' })}
                >
                  <input {...getInputProps()} />
                  <Flex
                    w='40px'
                    h='40px'
                    alignItems='center'
                    justifyContent='center'
                    borderRadius={PLAN_REVAMP_RADIUS_SM}
                    bg={colors.activeBg}
                    color={colors.primary}
                  >
                    <FiUploadCloud size={20} />
                  </Flex>
                  <Text {...PLAN_TEXT.bodyMedium} color={colors.textDark} textAlign='center'>
                    Drop your file here
                  </Text>
                  <Text {...PLAN_TEXT.body} color={colors.textSecondary} textAlign='center'>
                    or{' '}
                    <Text as='span' color={colors.primary} fontWeight={500}>
                      browse files
                    </Text>
                  </Text>
                  <Text {...PLAN_TEXT.labelSm} color={colors.textSecondary} mt={2}>
                    {`Supported formats: ${DATA_IMPORT_SUPPORTED_LABEL}`}
                  </Text>
                </Flex>
              )}
            </Dropzone>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default AddSourceModal;
